import React from 'react';
/* eslint-disable import/no-unresolved, node/no-missing-import */
import {
  PortableText,
  type PortableTextComponents,
  type PortableTextMarkComponentProps,
} from '@portabletext/react';
/* eslint-enable import/no-unresolved, node/no-missing-import */
import { Checkbox } from 'antd';
import { useEscDossierContext } from '../DossierContext';
import { getBlockContent, isBlockIncluded, resolveRaw } from './artifactSchema';
import styles from './DocumentView.module.scss';

interface Props {
  uiSchema: Record<string, any>;
  data: Record<string, unknown>;
  variant?: 'card' | 'flat';
  overrides?: Record<string, boolean | string>;
  activeBlockId?: string | null;
  showHighlights?: boolean;
  showCitations?: boolean;
  activeFieldPath?: string | null;
  onFieldClick?: (path: string) => void;
}

interface ResolvedCitation {
  file_id: string;
  local_page: number;
  file_name?: string;
}

interface CitationCtx {
  numbers: Map<string, number>;
  onCite: (c: ResolvedCitation) => void;
  showCitations: boolean;
}

const CitationContext = React.createContext<CitationCtx>({
  numbers: new Map(),
  onCite: () => {},
  showCitations: true,
});

interface FieldHighlightCtx {
  showHighlights: boolean;
  activeFieldPath: string | null | undefined;
  onFieldClick?: (path: string) => void;
  nonEditablePaths: Set<string>;
}

const FieldHighlightContext = React.createContext<FieldHighlightCtx>({
  showHighlights: false,
  activeFieldPath: null,
  nonEditablePaths: new Set(),
});

function computeNonEditablePaths(uiSchema: any): Set<string> {
  const paths = new Set<string>();
  const collect = (node: any) => {
    if (!node || typeof node !== 'object') {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(collect);
      return;
    }
    if (node._type === 'field' && node.path) {
      paths.add(node.path);
    }
    Object.values(node).forEach(collect);
  };
  for (const block of uiSchema.blocks ?? []) {
    if (block.editable === false) {
      collect(block);
    }
  }
  return paths;
}

const FieldSpan: React.FC<{
  path: string;
  children: React.ReactNode;
}> = ({ path, children }) => {
  const { showHighlights, activeFieldPath, onFieldClick, nonEditablePaths } =
    React.useContext(FieldHighlightContext);
  if (nonEditablePaths.has(path)) {
    return <>{children}</>;
  }
  const isActive = activeFieldPath === path;
  const cls =
    [
      showHighlights ? styles.fieldHighlight : '',
      isActive ? styles.fieldHighlightActive : '',
    ]
      .filter(Boolean)
      .join(' ') || undefined;
  if (!cls && !onFieldClick) {
    return <>{children}</>;
  }
  return (
    <span
      id={`field-${path.replace(/\./g, '-')}`}
      className={cls}
      onClick={onFieldClick ? () => onFieldClick(path) : undefined}
    >
      {children}
    </span>
  );
};

function isResolved(c: unknown): c is ResolvedCitation {
  return (
    !!c &&
    typeof c === 'object' &&
    typeof (c as any).file_id === 'string' &&
    typeof (c as any).local_page === 'number'
  );
}

function citeKey(c: ResolvedCitation): string {
  return `${c.file_id}:${c.local_page}`;
}

const CitationChips: React.FC<{ citations?: unknown[] }> = ({
  citations = [],
}) => {
  const { numbers, onCite, showCitations } = React.useContext(CitationContext);
  if (!showCitations) {
    return null;
  }
  const resolved = citations.filter(isResolved);
  if (resolved.length === 0) {
    return null;
  }
  return (
    <>
      {resolved.map((c, i) => (
        <span
          key={i}
          className={styles.cite}
          role="button"
          tabIndex={0}
          onClick={() => onCite(c)}
          title={
            c.file_name
              ? `${c.file_name} · p.${c.local_page}`
              : `p.${c.local_page}`
          }
        >
          {numbers.get(citeKey(c))}
        </span>
      ))}
    </>
  );
};

function formatRaw(raw: unknown, format?: string): React.ReactNode | null {
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'boolean') {
    return raw ? 'Yes' : 'No';
  }
  if (typeof raw === 'number') {
    if (format === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(raw);
    }
    if (format === 'percent') {
      return `${raw}%`;
    }
    return raw.toLocaleString('en-US');
  }
  if (typeof raw === 'string' && (format === 'date' || format === 'datetime')) {
    try {
      let d: Date;
      if (format === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [y, m, day] = raw.split('-').map(Number);
        d = new Date(y, m - 1, day);
      } else {
        d = new Date(raw);
      }
      const opts: Intl.DateTimeFormatOptions =
        format === 'datetime'
          ? {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            }
          : { month: 'short', day: 'numeric', year: 'numeric' };
      return (
        <span style={{ whiteSpace: 'nowrap' }}>
          {new Intl.DateTimeFormat('en-US', opts).format(d)}
        </span>
      );
    } catch {
      return raw;
    }
  }
  return String(raw);
}

function resolveField(
  data: any,
  path: string,
  format?: string
): { display: React.ReactNode | null; citations: unknown[] } {
  const display = formatRaw(resolveRaw(data, path), format);

  // Field citations live on the parent object's citations array, keyed by field.
  const segments = path.split('.');
  const fieldName = segments[segments.length - 1];
  const parentPath = segments.slice(0, -1).join('.');
  const parent = resolveRaw(data, parentPath || '_self') as any;
  const citations = Array.isArray(parent?.citations)
    ? parent.citations.filter((c: any) => c.field === fieldName)
    : [];

  return { display, citations };
}

function collectCitations(value: any, map: Map<string, number>): void {
  if (!value || typeof value !== 'object') {
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectCitations(item, map));
    return;
  }
  if (Array.isArray(value.citations)) {
    for (const c of value.citations) {
      if (isResolved(c)) {
        const key = citeKey(c);
        if (!map.has(key)) {
          map.set(key, map.size + 1);
        }
      }
    }
  }
  for (const v of Object.values(value)) {
    collectCitations(v, map);
  }
}

function buildCitationNumbers(data: any): Map<string, number> {
  const map = new Map<string, number>();
  collectCitations(data, map);
  return map;
}

// Resolves a field from `source` at `path` and renders it: a checkbox for
// boolean fields, otherwise the formatted value plus its citation chips.
// When `spanPath` is set the result is wrapped in a clickable/highlightable
// FieldSpan keyed by that path; when null/undefined the bare content is shown.
const ResolvedField: React.FC<{
  source: any;
  path: string;
  format?: string;
  spanPath?: string | null;
}> = ({ source, path, format, spanPath }) => {
  if (format === 'boolean') {
    const cell = renderBooleanCell(resolveRaw(source, path));
    return spanPath ? (
      <FieldSpan path={spanPath}>{cell}</FieldSpan>
    ) : (
      <>{cell}</>
    );
  }
  const { display, citations } = resolveField(source, path, format);
  const inner = (
    <>
      {display ?? '—'}
      <CitationChips citations={citations} />
    </>
  );
  return spanPath ? <FieldSpan path={spanPath}>{inner}</FieldSpan> : inner;
};

const InlineContent: React.FC<{ nodes: unknown; data: any }> = ({
  nodes,
  data,
}) => {
  if (!Array.isArray(nodes)) {
    return null;
  }
  return (
    <>
      {nodes.map((node: any, i: number) => {
        if (node._type === 'field') {
          return (
            <ResolvedField
              key={i}
              source={data}
              path={node.path}
              spanPath={node.path}
            />
          );
        }
        const marks: string[] = Array.isArray(node.marks) ? node.marks : [];
        let content: React.ReactNode = node.text ?? '';
        if (marks.includes('strong')) {
          content = <strong>{content}</strong>;
        }
        if (marks.includes('em')) {
          content = <em>{content}</em>;
        }
        return <React.Fragment key={i}>{content}</React.Fragment>;
      })}
    </>
  );
};

type ResolvedRow = {
  label: string;
  cell: React.ReactNode;
  citations: unknown[];
};

function renderBooleanCell(raw: unknown): React.ReactNode {
  return <Checkbox checked={!!raw} onChange={() => {}} />;
}

function makeComponents(data: any): PortableTextComponents {
  const FieldNode = ({ value: node }: { value: any }) => {
    const marks: string[] = node.marks ?? [];
    const content = (
      <ResolvedField
        source={data}
        path={node.path}
        format={node.format}
        spanPath={node.path}
      />
    );
    return marks.includes('strong') ? (
      <strong>{content}</strong>
    ) : (
      <>{content}</>
    );
  };

  const TableNode = ({ value: node }: { value: any }) => {
    const rows: any[] = (resolveRaw(data, node.rows_path) as any[]) ?? [];
    const columns: any[] = node.columns ?? [];
    const hasRowCites = rows.some((r) =>
      Array.isArray(r.citations)
        ? r.citations.some((c: any) => !c.field)
        : false
    );
    return (
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((col: any, i: number) => (
              <th key={i} className={styles.th}>
                {col.header}
              </th>
            ))}
            {hasRowCites && <th className={styles.th}>Source</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row: any, ri: number) => {
            const rowCites = Array.isArray(row.citations)
              ? row.citations.filter((c: any) => !c.field)
              : [];
            return (
              <tr key={ri}>
                {columns.map((col: any, ci: number) => {
                  const cellPath =
                    col.path && col.path !== '_self'
                      ? `${node.rows_path}.${ri}.${col.path}`
                      : null;
                  return (
                    <td key={ci} className={styles.td}>
                      <ResolvedField
                        source={row}
                        path={col.path}
                        format={col.format}
                        spanPath={cellPath}
                      />
                    </td>
                  );
                })}
                {hasRowCites && (
                  <td className={styles.td}>
                    <CitationChips citations={rowCites} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const resolveRows = (
    fields: Array<{
      label: string;
      path?: string;
      value?: string;
      format?: string;
    }>,
    source: any,
    fullPath?: boolean
  ): ResolvedRow[] => {
    const result: ResolvedRow[] = [];
    for (const f of fields) {
      // A row may carry a fixed literal value (constant for the memo type)
      // instead of a data path.
      if (f.value != null && !f.path) {
        result.push({ label: f.label, cell: f.value, citations: [] });
        continue;
      }
      if (f.format === 'boolean') {
        const boolCell = renderBooleanCell(resolveRaw(source, f.path ?? ''));
        result.push({
          label: f.label,
          cell:
            fullPath && f.path ? (
              <FieldSpan path={f.path}>{boolCell}</FieldSpan>
            ) : (
              boolCell
            ),
          citations: [],
        });
      } else {
        const { display, citations } = resolveField(
          source,
          f.path ?? '',
          f.format
        );
        if (display != null || (fullPath && f.path)) {
          const cell =
            fullPath && f.path ? (
              <FieldSpan path={f.path}>
                {display ?? '—'}
                <CitationChips citations={citations} />
              </FieldSpan>
            ) : (
              display
            );
          result.push({
            label: f.label,
            cell,
            citations: fullPath ? [] : citations,
          });
        }
      }
    }
    return result;
  };

  const KeyValueTable: React.FC<{
    rows: ResolvedRow[];
    columnsPerRow: number;
    tableKey?: number;
    header?: string[];
  }> = ({ rows, columnsPerRow, tableKey, header }) => {
    if (rows.length === 0) {
      return null;
    }
    const chunks: ResolvedRow[][] = [];
    for (let i = 0; i < rows.length; i += columnsPerRow) {
      chunks.push(rows.slice(i, i + columnsPerRow));
    }
    return (
      <table key={tableKey} className={styles.table}>
        {header && header.length > 0 && (
          <thead>
            <tr>
              {header.map((h, i) => (
                <th key={i} className={styles.th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {chunks.map((chunk, ci) => (
            <tr key={ci}>
              {chunk.map((row, ri) => (
                <React.Fragment key={ri}>
                  <td className={styles.tdLabel}>{row.label}</td>
                  <td className={styles.td}>
                    {row.cell}
                    <CitationChips citations={row.citations} />
                  </td>
                </React.Fragment>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const KeyValueTableNode = ({ value: node }: { value: any }) => (
    <KeyValueTable
      rows={resolveRows(node.rows ?? [], data, true)}
      columnsPerRow={node.columns_per_row ?? 1}
      header={node.header}
    />
  );

  const ArrayKeyValueTableNode = ({ value: node }: { value: any }) => {
    const items: any[] = (resolveRaw(data, node.rows_path) as any[]) ?? [];
    const columnsPerRow: number = node.columns_per_row ?? 1;
    if (items.length === 0) {
      return null;
    }
    return (
      <>
        {items.map((item, ii) => {
          const fields: Array<{
            label: string;
            path: string;
            format?: string;
          }> = node.fields ?? [];
          const rows: ResolvedRow[] = [];
          for (const f of fields) {
            if (!f.path || f.path === '_self') {
              continue;
            }
            const fullPath = `${node.rows_path}.${ii}.${f.path}`;
            if (f.format === 'boolean') {
              rows.push({
                label: f.label,
                cell: (
                  <FieldSpan path={fullPath}>
                    {renderBooleanCell(resolveRaw(item, f.path))}
                  </FieldSpan>
                ),
                citations: [],
              });
            } else {
              const { display, citations } = resolveField(
                item,
                f.path,
                f.format
              );
              if (display != null) {
                rows.push({
                  label: f.label,
                  cell: (
                    <FieldSpan path={fullPath}>
                      {display}
                      <CitationChips citations={citations} />
                    </FieldSpan>
                  ),
                  citations: [],
                });
              }
            }
          }
          return (
            <KeyValueTable key={ii} rows={rows} columnsPerRow={columnsPerRow} />
          );
        })}
      </>
    );
  };

  const BooleanSectionNode = ({ value: node }: { value: any }) => {
    const fields: Array<{ label: string; path: string }> = node.fields ?? [];
    return (
      <div className={styles.booleanSection}>
        {node.heading && (
          <div className={styles.booleanSectionHeading}>{node.heading}</div>
        )}
        <div className={styles.booleanGrid}>
          {fields.map((f, i) => {
            const raw = resolveRaw(data, f.path);
            return (
              <FieldSpan key={i} path={f.path}>
                <label className={styles.booleanItem}>
                  <Checkbox checked={!!raw} onChange={() => {}} />
                  {f.label}
                </label>
              </FieldSpan>
            );
          })}
        </div>
      </div>
    );
  };

  return {
    types: {
      field: FieldNode,
      table: TableNode,
      key_value_table: KeyValueTableNode,
      array_key_value_table: ArrayKeyValueTableNode,
      boolean_section: BooleanSectionNode,
    },
    marks: {
      strong: ({ children }: PortableTextMarkComponentProps) => (
        <strong>{children}</strong>
      ),
      em: ({ children }: PortableTextMarkComponentProps) => <em>{children}</em>,
    },
    block: {
      normal: ({ children }) => <p className={styles.paragraph}>{children}</p>,
      // Preserves newlines from the source (white-space: pre-line).
      multiline: ({ children }) => (
        <p className={`${styles.paragraph} ${styles.multiline}`}>{children}</p>
      ),
      h1: ({ children }) => <h1 className={styles.blockHeading}>{children}</h1>,
      h2: ({ children }) => <h2 className={styles.blockHeading}>{children}</h2>,
      h3: ({ children }) => <h3 className={styles.subHeading}>{children}</h3>,
    },
    list: {
      bullet: ({ children }) => <ul className={styles.list}>{children}</ul>,
      number: ({ children }) => <ol className={styles.list}>{children}</ol>,
    },
    listItem: {
      bullet: ({ children }) => <li className={styles.listItem}>{children}</li>,
      number: ({ children }) => <li className={styles.listItem}>{children}</li>,
    },
  };
}

const DocumentView: React.FC<Props> = ({
  uiSchema,
  data,
  variant = 'card',
  overrides,
  activeBlockId,
  showHighlights,
  showCitations = true,
  activeFieldPath,
  onFieldClick,
}) => {
  const { onFileClick } = useEscDossierContext();

  React.useEffect(() => {
    if (!activeBlockId) {
      return;
    }
    const el = document.getElementById(`block-${activeBlockId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeBlockId]);

  React.useEffect(() => {
    if (!activeFieldPath) {
      return;
    }
    const id = `field-${activeFieldPath.replace(/\./g, '-')}`;
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeFieldPath]);

  const citationNumbers = React.useMemo(
    () => buildCitationNumbers(data),
    [data]
  );

  const citationCtx = React.useMemo<CitationCtx>(
    () => ({
      numbers: citationNumbers,
      onCite: (c) => onFileClick(c.file_id, c.local_page),
      showCitations,
    }),
    [citationNumbers, onFileClick, showCitations]
  );

  const components = React.useMemo(() => makeComponents(data), [data]);

  const blocks: any[] = uiSchema.blocks ?? [];
  const includedBlocks = blocks.filter((b) =>
    isBlockIncluded(b, data, overrides)
  );

  const nonEditablePaths = React.useMemo(
    () => computeNonEditablePaths(uiSchema),
    [uiSchema]
  );

  const fieldHighlightCtx: FieldHighlightCtx = React.useMemo(
    () => ({
      showHighlights: !!showHighlights,
      activeFieldPath,
      onFieldClick,
      nonEditablePaths,
    }),
    [showHighlights, activeFieldPath, onFieldClick, nonEditablePaths]
  );

  return (
    <FieldHighlightContext.Provider value={fieldHighlightCtx}>
      <CitationContext.Provider value={citationCtx}>
        <div className={styles.page}>
          <div
            className={
              variant === 'card' ? styles.documentCard : styles.document
            }
          >
            {includedBlocks.map((block) => {
              if (block.kind === 'letterhead') {
                if (Array.isArray(block.columns)) {
                  return (
                    <div key={block.id} className={styles.letterheadColumns}>
                      {block.columns.map((col: any, ci: number) => (
                        <div key={ci} className={styles.letterheadCol}>
                          {(col ?? []).map((line: unknown, li: number) => (
                            <div key={li}>
                              <InlineContent nodes={line} data={data} />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                }
                return (
                  <div key={block.id} className={styles.letterhead}>
                    <div className={styles.carrier}>
                      <InlineContent nodes={block.carrier} data={data} />
                    </div>
                    <div className={styles.letterMeta}>
                      {(block.meta ?? []).map((line: unknown, i: number) => (
                        <div key={i}>
                          <InlineContent nodes={line} data={data} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              if (block.kind === 'address') {
                return (
                  <div key={block.id} className={styles.address}>
                    {(block.lines ?? []).map((line: unknown, i: number) => (
                      <div key={i}>
                        <InlineContent nodes={line} data={data} />
                      </div>
                    ))}
                  </div>
                );
              }
              if (block.kind === 'aside') {
                return (
                  <div key={block.id} className={styles.aside}>
                    {(block.lines ?? []).map((line: unknown, i: number) => (
                      <div key={i}>
                        <InlineContent nodes={line} data={data} />
                      </div>
                    ))}
                  </div>
                );
              }
              if (block.kind === 're_line') {
                return (
                  <p key={block.id} className={styles.reLine}>
                    <strong>RE:</strong>&nbsp;
                    <InlineContent nodes={block.content} data={data} />
                  </p>
                );
              }
              if (block.kind === 'salutation') {
                return (
                  <p key={block.id} className={styles.salutation}>
                    <InlineContent nodes={block.content} data={data} />
                  </p>
                );
              }
              if (block.kind === 'footer') {
                return (
                  <div key={block.id} className={styles.footer}>
                    {(block.lines ?? []).map((line: unknown, i: number) => (
                      <div key={i}>
                        <InlineContent nodes={line} data={data} />
                      </div>
                    ))}
                  </div>
                );
              }
              const sectionCites: unknown[] = [];
              if (block.citations_path) {
                const raw = resolveRaw(data, block.citations_path);
                if (Array.isArray(raw)) {
                  sectionCites.push(...raw.filter((c: any) => !c.field));
                }
              }
              const isActive = activeBlockId === block.id;
              return (
                <div
                  key={block.id}
                  id={`block-${block.id}`}
                  className={`${styles.block}${
                    isActive ? ` ${styles.blockActive}` : ''
                  }`}
                >
                  <div className={styles.blockHeadingRow}>
                    {block.heading && (
                      <h3 className={styles.blockHeading}>{block.heading}</h3>
                    )}
                    <CitationChips citations={sectionCites} />
                  </div>
                  <PortableText
                    value={getBlockContent(block, data, overrides)}
                    components={components}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </CitationContext.Provider>
    </FieldHighlightContext.Provider>
  );
};

export default DocumentView;
